<template>
  <div class="is-grid gap-xs pad row">
    <div class="is-flex align-center justify-start wrap-text pad-xs">
      {{ resource.displayName }}
    </div>
    <div class="pad-xs is-flex-wrap gap-sm align-start">
      <div
        v-if="!availableModifierGroups.length && !loadingModifiers"
        class="is-flex align-center justify-center is-full-width"
      >
        {{ noModifiersMessage }}
      </div>
      <div
        v-for="modifier in resourceSelectedModifiers"
        :key="modifier.id"
        class="tag is-primary is-light auto-height mar-none pad-y-xs pad-x-sm"
      >
        <div>
          <p class="has-text-weight-bold text-ellipsis tag-max">{{ modifier.displayName }}</p>
          <p class="has-text-primary-dark">{{ modifier.id }}</p>
        </div>
        <button
          v-if="!readOnly"
          type="button"
          class="delete mar-none mar-l-sm has-background-primary-dark has-hover-background-primary-medium"
          @click.stop="updateResourceRestrictedModifiers({ removedModifierIds: [modifier.id] })"
        />
      </div>
    </div>
    <div class="is-flex align-center justify-center">
      <b-button
        :disabled="!availableModifierGroups.length || readOnly"
        type="is-primary is-light"
        icon-left="plus"
        :loading="loadingModifiers"
        class="mar-xs"
        @click="openModifierRestrictionModal(resource.id)"
      >
        Modifiers
      </b-button>
    </div>
  </div>
</template>

<script>
  import searchByGroupSelectModal from '@/components/globals/search-by-group-select-modal.vue';

  import getChangedResources from '@/helpers/get-changed-resources';

  import ModifierGroup from '@/store/classes/ModifierGroup';

  export default {
    name: 'ModifierRestrictionRow',

    props: {
      resource: {
        type: Object,
        required: true
      },

      allResourcesSelectedModifierIds: {
        type: Array,
        default: () => []
      },

      isCategorySearch: {
        type: Boolean,
        required: true
      },

      readOnly: {
        type: Boolean,
        required: true
      }
    },

    data() {
      return {
        availableModifierGroups: [],
        loadingModifiers: false
      };
    },

    computed: {
      resourceSelectedModifiers() {
        return this.allResourcesSelectedModifierIds.map((id) => {
          const modifier = this.flattenedModifiers.find(mod => mod.id === id);
          return modifier ? { ...modifier, exists: true } : false;
        }).filter(Boolean);
      },

      resourceSelectedModifierIds() {
        return this.resourceSelectedModifiers.map(modifier => modifier.id);
      },

      flattenedModifiers() {
        return this.availableModifierGroups.flatMap(modifierGroup => modifierGroup.items);
      },

      noModifiersMessage() {
        return `No modifiers available for the selected ${this.isCategorySearch ? 'category' : 'item'}`;
      }
    },

    async created() {
      await this.fetchModifiers();
    },

    methods: {
      async fetchModifiers() {
        this.loadingModifiers = true;

        try {
          const itemType = this.isCategorySearch ? 'category' : 'item';
          const { menuItemModifierGroups } = await ModifierGroup.fetchModifierGroups({ [`menu_${itemType}_ids`]: [this.resource.id] });
          this.availableModifierGroups = menuItemModifierGroups.map(itemModifierGroup => (
            {
              id: itemModifierGroup.modifierGroupId,
              displayName: itemModifierGroup.displayName,
              createdDate: itemModifierGroup.createdDate,
              items: itemModifierGroup.menuItemModifiers.map(modifier => (
                {
                  id: modifier.menuItemId,
                  displayName: modifier.displayName
                }
              ))
            }
          )).sort((a, b) => a.displayName.localeCompare(b.displayName) || new Date(b.createdDate) - new Date(a.createdDate));
        }
        catch (error) {
          this.$_onRequestError({
            toastOptions: {
              message: 'There was an error fetching the modifiers'
            },
            error
          });
        }

        finally {
          this.loadingModifiers = false;
        }
      },

      openModifierRestrictionModal() {
        this.$buefy.modal.open({
          parent: this,
          component: searchByGroupSelectModal,
          hasModalCard: true,
          trapFocus: true,
          canCancel: false,
          customClass: 'auto-width',
          props: {
            title: 'Modifier Restriction',
            subtitle: 'Only modifiers shared by all selected items are available',
            confirmText: 'Add',
            placeholderText: 'Search for a modifier...',
            groups: this.availableModifierGroups,
            selectedIds: [...this.resourceSelectedModifierIds],
            onSubmit: modifierIds => this.determineAddedAndRemovedModifierIds(modifierIds)
          }
        });
      },

      determineAddedAndRemovedModifierIds(modifierIds) {
        const { added, removed } = getChangedResources({
          newArray: modifierIds,
          oldArray: this.resourceSelectedModifierIds
        });

        this.updateResourceRestrictedModifiers({ addedModifierIds: added, removedModifierIds: removed });
      },

      updateResourceRestrictedModifiers({ addedModifierIds = [], removedModifierIds = [] } = {}) {
        this.$emit('update-resource-restricted-modifiers', { addedModifierIds, removedModifierIds });
      }
    }
  };
</script>

<style lang="sass" scoped>
  .row
    grid-template-columns: 150px 1fr 150px

  .tag
    display: flex
    align-items: center
    justify-content: space-between
    padding: 0.5rem
    max-width: 240px

  .tag-max
    max-width: 180px
</style>
